import React, { useCallback, useState } from "react";
import styled from "styled-components";
import Tooltip from "@mui/material/Tooltip";
import { useResponsive } from "../Hooks/index";
import { Box, FlexBox } from "../Boxes/index";
import Icon from "../Icons/index";
import Address from "./ProfileCardAddress";
import { Text } from "../Text/index";
import FormDialog from "../Modals/FormDialog";
import { formateDateToReadableFormatWithoutTime } from "../../utiles/dateformate";
import { ClientEditDialog } from "../../Pages/Team/Clients/Profile/Dialogs/ClientEditDialog";
import { StaffEditDialog } from "../../Pages/Team/Staff/Profile/Dialogs/EditStaffDialog";
import Contact from "./ProfileCardContact";
import { color, radius, shadow, textSize, fontFamily } from "../Theme";
import usePermissionsController from "../../permissions";
import ProfilePhoto from "../Images/ProfilePhoto";
import { Button } from "../Input";
import { Chip, Muted } from "../DataTable";
import { SearchUserSelectBox } from "../../Pages/components/searchSelectBox";
import { useSendStaffProfileErrorMessageSelector, useSendStaffProfileStatusSelector, useSendStaffProfileSuccessSelector } from "../../redux/selectors/useClientSelectors";
import useClientDispatchers from "../../redux/dispatchers/useClientDispatchers";
import { sleepForMilliseconds } from "../../utiles/timers";
import * as asyncStates from "../../redux/constants/asyncStates";
import CircularLoader from "../Icons/circularLoader";
import BasicAlerts from "../Boxes/ErrorAndSuccess";
import { useLoggedInStaffId } from "../../hooks";

// Profile header card + details list for staff and clients. Colours, radius and
// shadow come from the per-brand tokens only, so GPS Care keeps its own theme
// and TLScare gets its own.
const Card = styled.section`
  width: 100%;
  background: ${color("surface")};
  border: 1px solid ${color("border")};
  border-radius: ${() => radius("card")}px;
  box-shadow: ${() => shadow("card")};
  color: ${color("text")};
  font-family: ${() => fontFamily()};
  font-size: ${() => textSize("body-sm")}px;
  overflow: clip;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  @media (max-width: 599px) {
    padding: 16px;
    gap: 12px;
  }
`;

const PhotoWrap = styled.div`
  flex: none;
`;

const Identity = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Name = styled.h1`
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  color: ${color("heading")};
  overflow-wrap: anywhere;
  @media (max-width: 599px) {
    font-size: 20px;
    line-height: 28px;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ContactLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  color: ${color("text-muted")};
  overflow-wrap: anywhere;
`;

const EditButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid ${color("border")};
  border-radius: ${() => radius("control")}px;
  background: ${color("surface")};
  color: ${color("primary")};
  cursor: pointer;
  &:hover { background: ${color("list")}; }
  &:focus-visible { outline: 2px solid ${color("primary")}; outline-offset: 2px; }
  svg { color: inherit !important; }
`;

const Details = styled.dl`
  margin: 0;
  padding: 20px 24px;
  border-top: 1px solid ${color("border")};
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 32px;
  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
    padding: 16px;
  }
`;

const DetailItem = styled.div`
  min-width: 0;
  dt {
    color: ${color("text-muted")};
    margin-bottom: 2px;
  }
  dd {
    margin: 0;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
`;

const Note = styled.div`
  margin: 0 24px 16px;
  padding: 12px 16px;
  background: #FEFF9C;
  border-radius: ${() => radius("small")}px;
  overflow-wrap: anywhere;
  @media (max-width: 599px) {
    margin: 0 16px 16px;
  }
`;

const NoteTitle = styled.div`
  font-weight: 700;
  font-style: italic;
  margin-bottom: 4px;
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${color("border")};
  @media (max-width: 599px) {
    padding: 16px;
  }
`;

export function ProfileCard({ data, type, $onImageChanged, $imageLoading }) {
  const isDesktop = useResponsive({ xs: false, sm: true });
  const [open, setOpen] = useState(false);
  const [searchUser, setSearchUser] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const { staffId: loggedInStaffId } = useLoggedInStaffId()
  function HandleDialog() {
    setOpen(!open);
  }
  const {
    clientDoctorDetailsSpecified,
    clientNextOfKinDetailsSpecified,
    clientTherapistDetailsSpecified,
    clientSocialworkerDetailsSpecified,
    accessSendStaffProfile,
    clientEmailSpecified,
    clientRefIdSpecified,
    clientClientNotesSpecified,
    clientDOBSpecified,
    clientGenderSpecified,
    clientPhoneNumberSpecified,
    staffJobRoleSpecified,

    editStaff,
    editOwnStaff,
    editClient,
  } = usePermissionsController()

  const canEditProfile = useCallback(() => {
    if (type === "ClientData") return editClient == true

    if (type === "StaffData") {
      if (editStaff) return true
      if (editOwnStaff && loggedInStaffId == data?.id) return true
    }
    return false
  }, [type, editStaff, editOwnStaff, loggedInStaffId, data?.id])

  const sendStaffProfileErrorMessage = useSendStaffProfileErrorMessageSelector()
  const sendStaffProfileStatus = useSendStaffProfileStatusSelector()
  const sendStaffProfileSuccessMessage = useSendStaffProfileSuccessSelector()

  const {
    clearAllSuccessMessages,
    clearAllStatusMessages,
    clearAllErrorMessages,
    sendStaffProfile
  } = useClientDispatchers()


  const sendStaffProfileHandler = async () => {
    await sendStaffProfile({
      staffId: selectedUser.id,
      clientId: data.id
    }).unwrap()
    await sleepForMilliseconds(1000 * 5)
    clearAllErrorMessages()
    clearAllStatusMessages()
    clearAllSuccessMessages()
    setSelectedUser(null)
    setSearchUser(null)
  }

  const detailItems = [
    type === "StaffData" && staffJobRoleSpecified ? {
      label: "Job Role",
      value: data?.jobRole,
    } : null,
    ((type === "ClientData" && clientRefIdSpecified) || type != "ClientData") ? {
      label: "Ref ID",
      value: data?.refId,
    } : null,
    ((type === "ClientData" && clientGenderSpecified) || type != "ClientData") ? {
      label: "Gender",
      value: data?.gender,
    } : null,
    ((type === "ClientData" && clientDOBSpecified) || type != "ClientData") ? {
      label: "DOB",
      value: data?.dateOfBirth ? formateDateToReadableFormatWithoutTime(data?.dateOfBirth) : null,
    } : null,
    {
      label: "Address",
      value: data?.address ? <Address data={data?.address} /> : null,
    },/**doctorDetails nextOfKin therapistDetails socialWorkerContacts */
    type === "ClientData" && clientDoctorDetailsSpecified ? {
      label: "Doctor Details",
      value: data?.doctorDetails,
    } : null,
    type === "ClientData" && clientNextOfKinDetailsSpecified ? {
      label: "Next Of Kin",
      value: data?.nextOfKin,
    } : null,
    type === "ClientData" && clientTherapistDetailsSpecified ? {
      label: "Therapist Details",
      value: data?.therapistDetails,
    } : null,
    type === "ClientData" && clientSocialworkerDetailsSpecified ? {
      label: "Social Worker",
      value: data?.socialWorkerContacts,
    } : null,
  ].filter(Boolean)

  const showJobRoleChip = type === "StaffData" && staffJobRoleSpecified && data?.jobRole
  const teams = data?.teams ?? []

  return (
    <>
      <Card aria-label={data?.name}>
        <Header>
          <PhotoWrap>
            <ProfilePhoto
              $size={isDesktop ? "88px" : "64px"}
              $loading={$imageLoading}
              $url={data?.profilePhotoUrl}
              $onImageChanged={$onImageChanged}
            />
          </PhotoWrap>
          <Identity>
            <Name>{data?.name}</Name>
            {(showJobRoleChip || teams.length > 0) && (
              <ChipRow>
                {showJobRoleChip && <Chip>{data.jobRole}</Chip>}
                {teams.map((team, index) => (
                  <Chip key={index}>{team.name}</Chip>
                ))}
              </ChipRow>
            )}
            {(clientEmailSpecified || clientPhoneNumberSpecified) && (
              <ContactLine>
                {clientEmailSpecified && <span>{data?.email}</span>}
                {clientPhoneNumberSpecified && (
                  <span>
                    {data?.contact ? (
                      <Contact data={data?.contact} />
                    ) : (
                      "Contact not provided"
                    )}
                  </span>
                )}
              </ContactLine>
            )}
          </Identity>
          {canEditProfile() && (
            <Tooltip title="Update Details">
              <EditButton type="button" aria-label="Update Details" onClick={HandleDialog}>
                <Icon $icon="ModeEdit" $size={20} />
              </EditButton>
            </Tooltip>
          )}
        </Header>

        <Details>
          {detailItems.map((item) => (
            <DetailItem key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value ? item.value : <Muted>Not Provided</Muted>}</dd>
            </DetailItem>
          ))}
          {teams.length > 0 && (
            <DetailItem>
              <dt>Team</dt>
              <dd>
                <ChipRow>
                  {teams.map((team, index) => (
                    <Chip key={index}>{team.name}</Chip>
                  ))}
                </ChipRow>
              </dd>
            </DetailItem>
          )}
        </Details>

        {data?.about && (
          <Note>
            <NoteTitle>About {type == "ClientData" ? "client" : "staff"}</NoteTitle>
            <div>{data?.about}</div>
          </Note>
        )}

        {clientClientNotesSpecified && data?.notes && (
          <Note>
            <NoteTitle>Client notes</NoteTitle>
            <div>{data?.notes}</div>
          </Note>
        )}

        {type === "ClientData" && accessSendStaffProfile && <Footer>
          {!searchUser && <Button $onClick={() => {
            setSearchUser(true)
          }}>Send staff profile</Button>}

          {searchUser && <Box $width="100%">
            <Text $fontSize="body" $marginBottom={1} >
              Staff
            </Text>

            {sendStaffProfileErrorMessage && <FlexBox $width="100%" $justifyContent="center" $marginBottom={1}>
              <BasicAlerts $type="error">{sendStaffProfileErrorMessage}</BasicAlerts>
            </FlexBox>}

            {sendStaffProfileSuccessMessage && <FlexBox $width="100%" $justifyContent="center" $marginBottom={1}>
              <BasicAlerts $type="success">{sendStaffProfileSuccessMessage}</BasicAlerts>
            </FlexBox>}

            <FlexBox $width="100%">
              <SearchUserSelectBox
                searchByStaff
                selected={selectedUser}
                onChange={(user) => {
                  setSelectedUser(user)
                }}
              />
              {sendStaffProfileStatus == asyncStates.PENDING ? <Box $marginLeft={2} $marginRight={2}>
                <CircularLoader $size={20} />
              </Box> : <Icon
                $icon="Send"
                $size={20}
                $color={selectedUser ? "primary" : "grey"}
                $pointer={selectedUser ? "pointer" : null}
                $marginLeft={2}
                $onClick={() => {
                  if (selectedUser) {
                    sendStaffProfileHandler()
                  }
                }} />}
              <Box $marginLeft={2} />
            </FlexBox>
          </Box>}
        </Footer>}
      </Card>
      <FormDialog
        $open={open}
        $handleDialog={HandleDialog}
        $title={"Update Details"}
        $maxWidth="sm"
        $fullWidth
        $fullScreen={!isDesktop}
      >
        {type === "ClientData" && (
          <ClientEditDialog
            $handleDialog={HandleDialog}
            $type="Clientedit"
            clientData={data}
          />
        )}
        {type === "StaffData" && (
          <StaffEditDialog
            $handleDialog={HandleDialog}
            staffData={data}
          />
        )}
      </FormDialog>
    </>
  );
}
